import React from "react";
import { useParams, Navigate } from "react-router-dom";
import Layout from "../../layout/Layout";
import SeoHead from "../../component/SeoHead";
import { pageRouteUrls } from "../../helper/pageRouteUrls";
import {
    getComparison,
    buildFaqSchema,
    COMPETITOR_PRICING_NOTES,
    PRICING_AS_OF,
    MY_STARTING_PRICE,
} from "../../data/comparisonData";
import "./marketing.css";

/**
 * Renders a MyEduLink vs <competitor> comparison landing page from the shared
 * comparison data. Reached at /vs/:competitor (teachable | thinkific | kajabi).
 */
const ComparisonPage: React.FC = () => {
    const { competitor } = useParams<{ competitor: string }>();
    const data = getComparison(competitor);

    // Unknown competitor slug → send visitor to the category page rather than 404.
    if (!data) return <Navigate to={pageRouteUrls.CheapestLiveTeaching()} replace />;

    const canonicalPath = pageRouteUrls.VsCompetitor(data.slug);
    const productSchema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "MyEduLink",
        description: data.metaDescription,
        brand: { "@type": "Brand", name: "MyEduLink" },
        offers: {
            "@type": "Offer",
            price: "19",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://myedulink.net/pricing",
        },
    };

    return (
        <Layout>
            <SeoHead
                title={data.title}
                description={data.metaDescription}
                canonicalPath={canonicalPath}
                jsonLd={[buildFaqSchema(data.faqs), productSchema]}
            />

            <div className="mk-page">
                <div className="container">

                    {/* Hero */}
                    <div className="mk-hero">
                        <span className="mk-eyebrow">{data.name} alternative</span>
                        <h1>MyEduLink vs <span>{data.name}</span></h1>
                        <p>{data.intro}</p>
                        <div className="mk-price-badge">
                            <i className="flaticon-badge"></i>
                            <span>Live classes, auto-grading &amp; certificates from {MY_STARTING_PRICE}</span>
                        </div>
                        <div className="mk-cta-row">
                            <a className="btn btn-primary btn-hover-dark" href={pageRouteUrls.Register()}>Start free</a>
                            <a className="btn btn-secondary btn-hover-primary" href={pageRouteUrls.Pricing()}>See pricing</a>
                        </div>
                        <div className="mk-reassure">Get paid directly — 2 months free, no credit card, cancel anytime.</div>
                    </div>

                    {/* Comparison table */}
                    <div className="mk-table-wrap">
                        <table className="mk-table">
                            <thead>
                                <tr>
                                    <th scope="col">&nbsp;</th>
                                    <th scope="col" className="mk-col-mine">MyEduLink</th>
                                    <th scope="col">{data.name}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map(row => (
                                    <tr key={row.label}>
                                        <td className="mk-row-label">{row.label}</td>
                                        <td className="mk-col-mine">
                                            {row.mineWins && <span className="mk-check">✓</span>}
                                            {row.mine}
                                        </td>
                                        <td>
                                            {!row.mineWins && <span className="mk-check">✓</span>}
                                            {row.mineWins && <span className="mk-dash">–</span>}
                                            {row.theirs}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mk-footnote">
                            Competitor details as of {PRICING_AS_OF}. {COMPETITOR_PRICING_NOTES[data.slug]}
                        </p>
                    </div>

                    {/* Migration-regret reassurance */}
                    <div className="mk-migration">
                        <h2>{data.migrationRegret.heading}</h2>
                        <p>{data.migrationRegret.body}</p>
                    </div>

                    {/* FAQ */}
                    <div className="mk-faq">
                        <h2>Frequently asked questions</h2>
                        {data.faqs.map(faq => (
                            <div className="mk-faq-item" key={faq.q}>
                                <h3>{faq.q}</h3>
                                <p>{faq.a}</p>
                            </div>
                        ))}
                    </div>

                    {/* Final CTA */}
                    <div className="mk-final-cta">
                        <h2>Try MyEduLink free for 2 months</h2>
                        <p>Run a live class, set an auto-graded exam and issue a certificate — no credit card required.</p>
                        <a className="btn btn-primary btn-hover-dark" href={pageRouteUrls.Register()}>Start free</a>
                    </div>

                </div>
            </div>
        </Layout>
    );
};

export default ComparisonPage;
